import * as React from "react";
import { getUsers } from "../../libs/user";
import { getOrgUsers } from "../../libs/organization";
import { useEffect, useState } from "react";
import usePromise from "../hooks/usePromise";
import { flatten, uniq } from "lodash";
import Loader from "../core/Loader";
import { getCourses } from "../../libs/courses";
import CourseAssignment from "./CourseAssignment";
import Panel from "../core/Panel";
import OrgPicker from "../core/OrgPicker";
import CenterDiv from "../core/CenterDiv";
import FilterInput from "../core/FilterInput";

interface IProps {
  userContext: IUserContextNormalized;
  selectedOrgId: string;
  setSelectedOrgId: (orgId: string) => void;
}

function ViewAssignments({
  userContext,
  selectedOrgId,
  setSelectedOrgId,
}: IProps) {
  const canChangeOrg = userContext.user.isAdmin;
  const [userFilter, setUserFilter] = useState("");

  /**
   * This is a big giant promise that gets the users we want to get,
   * then the course data for each of their assigned courses and overwrites the
   * courses property of the user with the course data
   */
  const [users, { loading }] = usePromise<INormalizedObject[]>(
    () =>
      (userContext.user.isManager
        ? getUsers(userContext.user.subordinates.map(s => s.id))
        : getOrgUsers(selectedOrgId)
      ).then(async users => {
        const usersWithCourses = users.filter(
          u => u.courses && u.courses.length > 0
        );

        const courseIds = uniq(
          flatten(usersWithCourses.map(u => u.courses.map(c => c.id)))
        );

        const courses = await getCourses(courseIds).then(courses => {
          const map = new Map();
          courses.forEach(course => {
            if (!map.has(course.id)) {
              map.set(course.id, course);
            }
          });
          return map;
        });

        return usersWithCourses.map(user => ({
          ...user,
          courses: user.courses.map(course => courses.get(course.id)),
        }));
      }),
    [selectedOrgId]
  );

  const filteredUsers = userFilter
    ? users.filter(user =>
        user.name.toUpperCase().includes(userFilter.toUpperCase())
      )
    : users;

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      {canChangeOrg && (
        <>
          <label>Selected Org</label>
          <OrgPicker
            selectedOrgId={selectedOrgId}
            setSelectedOrgId={setSelectedOrgId}
            userContext={userContext}
          />
          <hr />
        </>
      )}
      <FilterInput
        placeholder={"Filter by name"}
        value={userFilter}
        onChange={setUserFilter}
      />
      <p>&nbsp;</p>
      {users.length > 0 ? (
        filteredUsers.map(user => (
          <Panel
            key={user.id}
            heading={<h4>{user.name}'s Assignments</h4>}
            type={"info"}
            collapsible={true}
            startCollapsed={true}
            maxBodyHeight={`${150 * user.courses.length}px`}
            headerItemRight={
              <span className={"badge"}>{user.courses.length}</span>
            }
          >
            {user.courses.map(course => (
              <CourseAssignment key={course.id} course={course} user={user} />
            ))}
          </Panel>
        ))
      ) : (
        <>
          <CenterDiv>
            <i className="fa fa-3x fa-inbox" />
          </CenterDiv>
          <CenterDiv>
            <p>
              Nobody has any assigned courses yet, go to the Assign Courses tab
              to assign some.
            </p>
          </CenterDiv>
        </>
      )}
    </>
  );
}

export default ViewAssignments;



// WEBPACK FOOTER //
// ./app/javascript/components/courses/ViewAssignments.tsx